import { useSearchParentSessionFeedback } from '@actions';
import { LoadingSpinner } from '@components/LoadingSpinner/LoadingSpinner';
import TableHeaderInfoCard from '@components/V3/TableHeaderInfoCard';
import { PaginationProps } from '@components/V4/Pagination';
import { SortOrder } from '@components/V4/Table/Table';
import { useDebounce, useQueryParamsState } from '@hooks';
import ParentsHeader from '@modules/Admin/pages/ParentsSurvey/components/ParentsSurveyHeader';
import SessionFeedbackTable from './components/SessionFeedbackTable';
import moment, { Moment } from 'moment';
import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';

const useQuery = () => new URLSearchParams(useLocation().search);

const ParentSessionFeedback = () => {
  const [studentSearch, setStudentSearch] = useState('');
  const [mentorSearch, setMentorSearch] = useState('');
  const [parentSearch, setParentSearch] = useState('');

  const debouncedStudentSearch = useDebounce(studentSearch, 500);
  const debouncedMentorSearch = useDebounce(mentorSearch, 500);
  const debouncedParentSearch = useDebounce(parentSearch, 500);

  const query = useQuery();
  const limit = query.get('limit') || '10';
  const page = query.get('page') || '1';

  const course = query.get('course') || null;
  const student = query.get('student') || null;
  const mentor = query.get('mentor') || null;
  const parent = query.get('parent') || null;
  const startDate = query.get('startDate') || null;
  const endDate = query.get('endDate') || null;

  const sortBy = query.get('sortBy') || null;
  const sortOrder = (query.get('sortOrder') || null) as SortOrder;

  const [searchParams, setSearchParams] = useQueryParamsState<any>({
    page: page,
    limit: limit,
    course: course,
    student: student,
    mentor: mentor,
    parent: parent,
    sortBy: sortBy,
    sortOrder: sortOrder,
    startDate: startDate,
    endDate: endDate,
  });

  const [dateRange, setDateRange] = useState<[Moment, Moment] | null>(() => {
    if (startDate && endDate) {
      return [moment(startDate), moment(endDate)];
    }
    return null;
  });

  useEffect(() => {
    if (searchParams.startDate && searchParams.endDate) {
      setDateRange([moment(searchParams.startDate), moment(searchParams.endDate)]);
    } else {
      setDateRange(null);
    }
  }, [searchParams.startDate, searchParams.endDate]);

  const { data, isLoading } = useSearchParentSessionFeedback({
    page: searchParams.page,
    limit: searchParams.limit,
    course: searchParams.course,
    student: searchParams.student,
    mentor: searchParams.mentor,
    parent: searchParams.parent,
    sortBy: searchParams.sortBy,
    sortOrder: searchParams.sortOrder,
    startDate: searchParams.startDate,
    endDate: searchParams.endDate,
  });

  const pagination: PaginationProps = {
    pageSize: limit !== null ? parseInt(limit) : 10,
    total: data?.total,
    currentPage: page !== null ? parseInt(page) : 1,
    onCurrentPageChange: (currentPage: number) => {
      setSearchParams({
        page: currentPage,
      });
    },
    onPageSizeChange: (size: number) => {
      setSearchParams({
        limit: size,
      });
    },
  };

  const handleDateRangeChange = (value: [Moment | null, Moment | null] | null) => {
    if (!value || !value[0] || !value[1]) {
      setDateRange(null);
      setSearchParams({
        startDate: null,
        endDate: null,
        page: 1,
      });
      return;
    }

    const [start, end] = value as [Moment, Moment];
    setDateRange([start, end]);
    setSearchParams({
      startDate: start.startOf('day').toISOString(),
      endDate: end.endOf('day').toISOString(),
      page: 1,
    });
  };

  const aggregateAverage =
    typeof data?.aggregate?.starRatingAverage === 'number'
      ? data.aggregate.starRatingAverage.toFixed(2)
      : '-';

  return (
    <section className="flex flex-col gap-8">
      <ParentsHeader
        title="Parent Feedback"
        searchParams={searchParams}
        setSearchParams={setSearchParams}
        studentSearch={studentSearch}
        setStudentSearch={setStudentSearch}
        mentorSearch={mentorSearch}
        parentSearch={parentSearch}
        setMentorSearch={setMentorSearch}
        setParentSearch={setParentSearch}
        debouncedStudentSearch={debouncedStudentSearch}
        debouncedMentorSearch={debouncedMentorSearch}
        debouncedParentSearch={debouncedParentSearch}
        dateRangeValue={dateRange}
        onDateRangeChange={handleDateRangeChange}
        headerExtra={
          <TableHeaderInfoCard
            title={aggregateAverage}
            description="Average Star Rating"
            fullWidth
          />
        }
      />

      {isLoading ? (
        <div className="flex justify-center py-10">
          <LoadingSpinner size="large" />
        </div>
      ) : (
        <SessionFeedbackTable
          enrollments={data?.enrollments}
          isLoading={false}
          pagination={pagination}
        />
      )}
    </section>
  );
};

export default ParentSessionFeedback;
